import { ZodError } from 'zod';

import { deliveryAssetToMediaAsset, parseMediaDeliveryManifest } from './mediaDeliveryManifest';
import { installDeliveredMedia, LOCAL_MEDIA_REGISTRY, type FmMediaAsset } from './mediaRegistry';

const MANIFEST_URL = '/api/v1/media/manifest';

/**
 * Machine-readable reason a media catalog request fell back to the bundled
 * registry. Surfaced on `document.documentElement[data-media-catalog-error]`
 * so contract breaks self-explain in E2E artifacts instead of pinning the page
 * to a silent `fallback` status. Kept short and stable for assertion.
 *
 * Taxonomy (each reason names a distinct failure stage, so triage is not
 * misdirected):
 * - `network`        — the fetch itself rejected (offline, DNS, refused).
 * - `http-<status>`  — the endpoint answered but not with 2xx.
 * - `body-invalid`   — 2xx but the body is not parseable JSON.
 * - `schema-invalid` — JSON parsed but violates the Zod manifest contract.
 * - `install-failed` — manifest was schema-valid but a post-parse transform
 *                      or install step threw (e.g. non-allowlisted delivery
 *                      host, registry install failure).
 */
type MediaCatalogErrorReason =
  | `http-${number}`
  | 'body-invalid'
  | 'schema-invalid'
  | 'install-failed'
  | 'network';

function markFallback(reason: MediaCatalogErrorReason): void {
  // A failed refresh must atomically retire the delivered catalog.
  // Otherwise the document claims "fallback" while renderers keep serving stale
  // delivered bytes and attribution from the last successful release.
  installDeliveredMedia('local-fallback', {});
  document.documentElement.dataset.mediaCatalogStatus = 'fallback';
  document.documentElement.dataset.mediaCatalogError = reason;
  // A catalog version only describes a DELIVERED catalog. Remove any stale
  // version from an earlier successful init so status=fallback never carries
  // a lying version attribute next to it.
  delete document.documentElement.dataset.mediaCatalogVersion;
}

function markDelivered(catalogVersion: string): void {
  document.documentElement.dataset.mediaCatalogStatus = 'delivered';
  document.documentElement.dataset.mediaCatalogVersion = catalogVersion;
  delete document.documentElement.dataset.mediaCatalogError;
}

export async function initialiseMediaDelivery(): Promise<void> {
  let response: Response;
  try {
    response = await fetch(MANIFEST_URL, { headers: { Accept: 'application/json' } });
  } catch {
    // Fetch itself rejected (offline, DNS, connection refused).
    markFallback('network');
    return;
  }

  if (!response.ok) {
    markFallback(`http-${response.status}`);
    return;
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    // 2xx with an unparseable body (truncated response, HTML error page
    // served on the JSON route). Distinct from `network`: the wire worked,
    // the body did not.
    markFallback('body-invalid');
    return;
  }

  try {
    const manifest = parseMediaDeliveryManifest(payload);
    const delivered: Record<string, FmMediaAsset> = {};
    for (const record of manifest.assets) {
      const asset = deliveryAssetToMediaAsset(record);
      const local = LOCAL_MEDIA_REGISTRY[record.identity];
      if (asset && local) {
        delivered[record.identity] = {
          ...asset,
          cropProfiles: {
            ...local.cropProfiles,
            ...asset.cropProfiles,
          },
        };
      }
    }
    installDeliveredMedia(manifest.catalogVersion, delivered);
    markDelivered(manifest.catalogVersion);
  } catch (error) {
    // The bundled registry is the governed offline/error fallback. Record WHY
    // so a manifest contract break is observable rather than silent: a Zod
    // schema violation is the class that shipped undetected for weeks. Any
    // other throw here happened AFTER the body parsed — a post-parse
    // transform/install failure (e.g. non-allowlisted delivery host), never
    // a network condition.
    markFallback(error instanceof ZodError ? 'schema-invalid' : 'install-failed');
  }
}
